import _ from 'lodash'
import { v4 as uuidv4 } from 'uuid'
import React, { useEffect, useRef } from 'react'

import { FormulaComponentInfo } from 'shared-libs/components/entity/formula-component-registry'
import { FramesManager } from 'shared-libs/components/view/frames-manager'
import type { UI2FormulaFieldProps } from 'shared-libs/components/ui2/fields/ui2-formula-field/ui2-formula-field-props'

import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import { UI2FieldShell } from '../field-ui/field-shell/field-shell'
import { UI2NoValueAvailable } from '../field-ui/no-value-available/no-value-available'

// frames is renderer-injected; not in the cross-platform contract.
interface IUI2FormulaFieldWebExtras extends Omit<IBaseProps, 'children'> {
  errors?: any[]
  frames?: FramesManager
}

/**
 * @uiComponent
 */
export interface IUI2FormulaFieldProps extends UI2FormulaFieldProps, IUI2FormulaFieldWebExtras {}

function computeDisplayValue(
  computed: any,
  propValue: any,
  numDecimalPlaces?: number,
  shadowPrefix?: string,
): string | null {
  let value: any = !_.isNil(computed) ? computed : propValue
  if (numDecimalPlaces && !_.isNil(value)) {
    value = _.round(value, numDecimalPlaces).toFixed(numDecimalPlaces)
  }
  if (_.isNil(value)) {
    return null
  }
  const str = String(value)
  return shadowPrefix ? `${shadowPrefix}${str}` : str
}

export function UI2FormulaField(props: IUI2FormulaFieldProps) {
  const {
    visible = false,
    numDecimalPlaces, shadowPrefix,
    formulaRegistry, frames,
    label, tooltip, inlineHelpText, translationTable,
    isReadOnly, isDisabled, isRequired, isOptional, enablePrefilledMode,
    errors, value,
  } = props

  const mountID = useRef<string>(uuidv4())
  const valueRef = useRef<any>(null)
  const componentInfoRef = useRef<FormulaComponentInfo | null>(null)

  useEffect(() => {
    if (!formulaRegistry || !frames) return
    const info = new FormulaComponentInfo(mountID.current, valueRef, frames)
    componentInfoRef.current = info
    formulaRegistry.register(info)
    return () => {
      formulaRegistry.unregister(mountID.current)
      componentInfoRef.current = null
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    // VD-4558: frames could be a new object — keep componentInfo in sync.
    if (componentInfoRef.current && frames) {
      componentInfoRef.current.frames = frames
    }
  }, [frames])

  if (!visible) return null

  const displayValue = computeDisplayValue(valueRef.current, value, numDecimalPlaces, shadowPrefix)
  const hasValue = displayValue !== null && displayValue !== ''

  return (
    <UI2FieldShell
      label={label}
      tooltip={tooltip}
      inlineHelpText={inlineHelpText}
      translationTable={translationTable}
      isReadOnly={isReadOnly}
      isDisabled={isDisabled}
      isRequired={isRequired}
      isOptional={isOptional}
      enablePrefilledMode={enablePrefilledMode}
      suppressDefaultPrefilledEdit={true}
      errors={errors}
      value={displayValue}
    >
      {hasValue
        ? <span>{displayValue}</span>
        : <UI2NoValueAvailable />
      }
    </UI2FieldShell>
  )
}
